import React, { useState } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Clock } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";

const TextInput = ({ label, name, type = "text", value, onChange, required, testid, disabled }) => (
  <div className="sf-field">
    <input
      id={name}
      name={name}
      type={type}
      placeholder=" "
      value={value}
      onChange={onChange}
      required={required}
      disabled={disabled}
      data-testid={testid}
      style={disabled ? { opacity: 0.4, pointerEvents: "none" } : undefined}
    />
    <label htmlFor={name}>{label}</label>
  </div>
);

const HOURS_12 = ["12", "01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11"];
const MINUTES = ["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"];

const to12h = (value) => {
  // value: "HH:MM" 24h -> {h12, mm, period}
  if (!value) return { h12: "12", mm: "00", period: "PM" };
  const [hStr, mStr] = value.split(":");
  const h = parseInt(hStr || "12", 10);
  const period = h >= 12 ? "PM" : "AM";
  let h12 = h % 12;
  if (h12 === 0) h12 = 12;
  return { h12: String(h12).padStart(2, "0"), mm: mStr || "00", period };
};

const to24h = ({ h12, mm, period }) => {
  let h = parseInt(h12, 10) % 12;
  if (period === "PM") h += 12;
  return `${String(h).padStart(2, "0")}:${mm}`;
};

const TimePicker = ({ value, onChange, disabled }) => {
  const { h12, mm, period } = to12h(value);
  const set = (next) => onChange(to24h({ h12, mm, period, ...next }));
  const display = value ? `${h12}:${mm} ${period}` : "Select";
  return (
    <Popover>
      <PopoverTrigger
        disabled={disabled}
        className="sf-picker-trigger"
        data-testid="form-birth-time-trigger"
        type="button"
      >
        <Clock size={14} className="opacity-60" />
        <span>{display}</span>
      </PopoverTrigger>
      <PopoverContent
        className="w-72 p-0 sf-popover"
        align="start"
        data-testid="form-birth-time-popover"
      >
        <div className="grid grid-cols-3 gap-0">
          <div className="border-r border-[var(--sf-border)] max-h-60 overflow-y-auto">
            <div className="overline px-3 py-2 sticky top-0 bg-[var(--sf-surface-elev)]">Hour</div>
            {HOURS_12.map((h) => (
              <button
                key={h}
                type="button"
                onClick={() => set({ h12: h })}
                className={`w-full text-left px-4 py-1.5 text-sm transition-colors ${
                  h === h12 ? "bg-[var(--sf-velvet-plum)]/40 text-[var(--sf-leopard-gold)]" : "hover:bg-white/5"
                }`}
              >
                {h}
              </button>
            ))}
          </div>
          <div className="border-r border-[var(--sf-border)] max-h-60 overflow-y-auto">
            <div className="overline px-3 py-2 sticky top-0 bg-[var(--sf-surface-elev)]">Min</div>
            {MINUTES.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => set({ mm: m })}
                className={`w-full text-left px-4 py-1.5 text-sm transition-colors ${
                  m === mm ? "bg-[var(--sf-velvet-plum)]/40 text-[var(--sf-leopard-gold)]" : "hover:bg-white/5"
                }`}
              >
                {m}
              </button>
            ))}
          </div>
          <div className="max-h-60 overflow-y-auto">
            <div className="overline px-3 py-2 sticky top-0 bg-[var(--sf-surface-elev)]">Period</div>
            {["AM", "PM"].map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => set({ period: p })}
                className={`w-full text-left px-4 py-1.5 text-sm transition-colors ${
                  p === period ? "bg-[var(--sf-velvet-plum)]/40 text-[var(--sf-leopard-gold)]" : "hover:bg-white/5"
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const DatePicker = ({ value, onChange }) => {
  const date = value ? new Date(value + "T00:00:00") : undefined;
  return (
    <Popover>
      <PopoverTrigger
        className="sf-picker-trigger"
        data-testid="form-birth-date-trigger"
        type="button"
      >
        <CalendarIcon size={14} className="opacity-60" />
        <span>{date ? format(date, "MMM d, yyyy") : "Select date"}</span>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 sf-popover" align="start">
        <Calendar
          mode="single"
          captionLayout="dropdown-buttons"
          fromYear={1900}
          toYear={new Date().getFullYear()}
          selected={date}
          onSelect={(d) => d && onChange(format(d, "yyyy-MM-dd"))}
          initialFocus
          data-testid="form-birth-date-calendar"
        />
      </PopoverContent>
    </Popover>
  );
};

export const BirthForm = ({ onSubmit, loading }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    birth_date: "",
    birth_time: "",
    time_unknown: false,
    city: "",
    country: "",
  });

  const update = (k) => (e) =>
    setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(form);
  };

  return (
    <section
      id="birth-form"
      className="relative max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24"
      data-testid="birth-form-section"
    >
      <div className="text-center mb-10">
        <div className="overline mb-4">Step One — Your Birth Coordinates</div>
        <h2 className="font-display text-4xl md:text-5xl leading-tight">
          Where, when, and how you arrived.
        </h2>
        <p className="codex-text mt-6 max-w-xl mx-auto">
          Your information stays private. Your reading is yours.
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="sf-card p-8 md:p-12 space-y-1"
        data-testid="birth-form"
      >
        <div className="grid md:grid-cols-2 gap-x-10 gap-y-2">
          <TextInput
            label="Your Name"
            name="name"
            value={form.name}
            onChange={update("name")}
            required
            testid="form-name"
          />
          <TextInput
            label="Email Address"
            name="email"
            type="email"
            value={form.email}
            onChange={update("email")}
            required
            testid="form-email"
          />

          <div className="sf-picker-field">
            <div className="sf-picker-label">Birth Date</div>
            <DatePicker
              value={form.birth_date}
              onChange={(v) => setForm({ ...form, birth_date: v })}
            />
          </div>

          <div>
            <div className="sf-picker-field">
              <div className="sf-picker-label">Birth Time</div>
              <TimePicker
                value={form.birth_time}
                onChange={(v) => setForm({ ...form, birth_time: v })}
                disabled={form.time_unknown}
              />
            </div>
            <label className="sf-checkbox mt-3" data-testid="form-unknown-time-label">
              <input
                type="checkbox"
                checked={form.time_unknown}
                onChange={update("time_unknown")}
                data-testid="form-unknown-time-checkbox"
              />
              <span>I don't know my birth time (we'll use 12:00pm)</span>
            </label>
          </div>

          <TextInput
            label="Birth City"
            name="city"
            value={form.city}
            onChange={update("city")}
            required
            testid="form-city"
          />
          <TextInput
            label="Birth Country"
            name="country"
            value={form.country}
            onChange={update("country")}
            required
            testid="form-country"
          />
        </div>

        <div className="pt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <p className="text-xs tracking-wider uppercase text-[var(--sf-text-muted)] max-w-md">
            By submitting, you agree to receive your reading and occasional Soul
            Femme transmissions. Unsubscribe any time.
          </p>
          <button
            type="submit"
            className="sf-btn-primary"
            disabled={loading || !form.birth_date || (!form.time_unknown && !form.birth_time)}
            data-testid="form-submit-button"
          >
            {loading ? "Calculating…" : "Reveal My Codex"}
          </button>
        </div>
      </form>
    </section>
  );
};

export default BirthForm;
